import { useRef, useEffect, useCallback } from "react";
import { Slide } from "./Slide";
import { slideComponents } from "./SlideContainer";
import { INVESTOR_SLIDES } from "@/lib/constants";
import type { SlideMode, SlideEntry } from "@/lib/constants";
import { EXPANDED_W, COLLAPSED_W } from "./EditorSidebar";

interface SlideGalleryProps {
  onPresent: (slideIndex: number) => void;
  onActiveSlideChange: (index: number) => void;
  scrollToSlideRef: React.MutableRefObject<((index: number) => void) | null>;
  sidebarCollapsed: boolean;
  mode?: SlideMode;
  slides?: readonly SlideEntry[];
  components?: React.ComponentType<{ isActive: boolean }>[];
}

export function SlideGallery({
  onPresent,
  onActiveSlideChange,
  scrollToSlideRef,
  sidebarCollapsed,
  mode = "normal",
  slides = INVESTOR_SLIDES,
  components = slideComponents,
}: SlideGalleryProps) {
  const isExport = mode === "export";
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);

  const scrollToSlide = useCallback((index: number) => {
    slideRefs.current[index]?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  // Expose scrollToSlide to parent
  useEffect(() => {
    scrollToSlideRef.current = scrollToSlide;
  }, [scrollToSlide, scrollToSlideRef]);

  // IntersectionObserver for tracking which slide is most visible
  useEffect(() => {
    if (isExport) return;

    const observers: IntersectionObserver[] = [];

    slideRefs.current.forEach((el, i) => {
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry && entry.isIntersecting && entry.intersectionRatio > 0.5) {
            onActiveSlideChange(i);
          }
        },
        { threshold: 0.5 }
      );
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, [onActiveSlideChange, isExport]);

  return (
    <div
      className={isExport ? "" : "mt-[var(--topnav-height)] p-4 md:p-10 pb-20 flex flex-col gap-20 items-center transition-[margin-left] duration-200 ease-in-out"}
      style={isExport
        ? { margin: 0, padding: 0 }
        : { marginLeft: sidebarCollapsed ? COLLAPSED_W : EXPANDED_W, paddingBottom: "calc(80px + env(safe-area-inset-bottom, 0px))" }
      }
    >
      {/* Floating Present button */}
      {!isExport && (
        <button
          onClick={() => onPresent(0)}
          className="fixed right-6 z-[100] w-12 h-12 rounded-full flex items-center justify-center bg-[var(--color-accent)] text-white shadow-lg hover:scale-110 transition-transform duration-150"
          style={{ bottom: "calc(24px + env(safe-area-inset-bottom, 0px))" }}
          aria-label="Present"
        >
          <span className="text-[14px] ml-0.5">&#9654;</span>
        </button>
      )}

      {components.map((SlideContent, i) => (
        <div
          key={slides[i]!.id}
          ref={(el) => { slideRefs.current[i] = el; }}
          id={`slide-${slides[i]!.id}`}
          className={isExport ? "" : "w-full cursor-pointer"}
          style={isExport ? { pageBreakAfter: "always", pageBreakInside: "avoid" } : undefined}
          onDoubleClick={isExport ? undefined : () => onPresent(i)}
        >
          <Slide
            isActive={true}
            index={i}
            title={slides[i]!.label}
            badge={slides[i]!.badge}
            totalSlides={slides.length}
            mode={mode}
          >
            <SlideContent isActive={true} />
          </Slide>
        </div>
      ))}
    </div>
  );
}
